import {
  Anchor,
  AnchorHorizontal,
  AnchorVertical,
  Length,
  Position,
  Size,
  Unit,
  UnsupportedAnchorError,
} from "../../types/customization/box";
import { ContentEditablePicture, Cropping } from "../../types/customization/content";
import { Template } from "../../types/customization/template";
import { SelectionPhoto, SelectionState } from "../../types/selection/selection";
import { findPhoto } from "../selection/finders";
import { findEditablePictureDefinition, findPageDefinition } from "./finders";

export function calcAbsolutePosition<U extends Unit>({
  elementAnchor,
  elementPosition,
  elementSize,
  pageAnchor,
  pageSize,
}: {
  elementAnchor: Anchor;
  elementPosition: Position<U>;
  elementSize: Size<U>;
  pageAnchor: Anchor;
  pageSize: Size<U>;
}): Position<U> {
  const x = { ...elementPosition.x };
  const y = { ...elementPosition.y };

  switch (pageAnchor[0]) {
    case AnchorVertical.TOP:
      break;
    case AnchorVertical.MIDDLE:
      y.value += pageSize.height.value / 2;
      break;
    case AnchorVertical.BOTTOM:
      y.value += pageSize.height.value;
      break;
    default: {
      const exhaustiveCheck: never = pageAnchor[0];
      throw new UnsupportedAnchorError(exhaustiveCheck);
    }
  }

  switch (pageAnchor[1]) {
    case AnchorHorizontal.LEFT:
      break;
    case AnchorHorizontal.CENTER:
      x.value += pageSize.width.value / 2;
      break;
    case AnchorHorizontal.RIGHT:
      x.value += pageSize.width.value;
      break;
    default: {
      const exhaustiveCheck: never = pageAnchor[1];
      throw new UnsupportedAnchorError(exhaustiveCheck);
    }
  }

  switch (elementAnchor[0]) {
    case AnchorVertical.TOP:
      break;
    case AnchorVertical.MIDDLE:
      y.value -= elementSize.height.value / 2;
      break;
    case AnchorVertical.BOTTOM:
      y.value -= elementSize.height.value;
      break;
    default: {
      const exhaustiveCheck: never = elementAnchor[0];
      throw new UnsupportedAnchorError(exhaustiveCheck);
    }
  }

  switch (elementAnchor[1]) {
    case AnchorHorizontal.LEFT:
      break;
    case AnchorHorizontal.CENTER:
      x.value -= elementSize.width.value / 2;
      break;
    case AnchorHorizontal.RIGHT:
      x.value -= elementSize.width.value;
      break;
    default: {
      const exhaustiveCheck: never = elementAnchor[1];
      throw new UnsupportedAnchorError(exhaustiveCheck);
    }
  }

  return { x, y };
}

export function lengthP10kToMm(
  length: Length<Unit.P10K>,
  reference: Length<Unit.MM>
): Length<Unit.MM> {
  return {
    unit: Unit.MM,
    value: (reference.value * length.value) / 10000,
  };
}

export function lengthMmToPx(length: Length<Unit.MM>, ppmm: number): Length<Unit.PX> {
  return {
    unit: Unit.PX,
    value: length.value * ppmm,
  };
}

export function sizeP10kToMm(size: Size<Unit.P10K>, reference: Size<Unit.MM>): Size<Unit.MM> {
  return {
    height: lengthP10kToMm(size.height, reference.height),
    width: lengthP10kToMm(size.width, reference.width),
  };
}

export function sizeMmToPx(size: Size<Unit.MM>, ppmm: number): Size<Unit.PX> {
  return {
    height: lengthMmToPx(size.height, ppmm),
    width: lengthMmToPx(size.width, ppmm),
  };
}

export function positionP10kToMm(
  position: Position<Unit.P10K>,
  reference: Size<Unit.MM>
): Position<Unit.MM> {
  return {
    x: lengthP10kToMm(position.x, reference.width),
    y: lengthP10kToMm(position.y, reference.height),
  };
}

export function isLowQualityPicture(
  sizeMm: Size<Unit.MM>,
  selectionPhoto: SelectionPhoto,
  cropping: Cropping,
  minDpi = 150
): boolean {
  const mmToInRatio = 25.4;

  const croppedHeightPx = (selectionPhoto.height.value * cropping.height.value) / 10000;
  const croppedWidthPx = (selectionPhoto.width.value * cropping.width.value) / 10000;

  const heightIn = sizeMm.height.value / mmToInRatio;
  const widthIn = sizeMm.width.value / mmToInRatio;

  const dpiHeight = croppedHeightPx / heightIn;
  const dpiWidth = croppedWidthPx / widthIn;

  return dpiHeight < minDpi || dpiWidth < minDpi;
}

export function lowQualityPicturesByPage(
  pageDefinitionKey: string,
  pictures: ContentEditablePicture[],
  selection: SelectionState,
  template: Template
): number {
  const pageDefinition = findPageDefinition(pageDefinitionKey, template);
  let lowQualityPicturesCount = 0;

  pictures.forEach((picture) => {
    const elementDefinition = findEditablePictureDefinition(
      picture.editablePictureKey,
      pageDefinition
    );
    const elementSizeMm = sizeP10kToMm(elementDefinition, pageDefinition);
    const selectionPhoto = findPhoto(picture.selectionPhotoKey, selection);
    if (isLowQualityPicture(elementSizeMm, selectionPhoto, picture.cropping, template.minDpi)) {
      lowQualityPicturesCount++;
    }
  });

  return lowQualityPicturesCount;
}

export function getSizeRatio(width: number, height: number): number {
  if (!width || !height || width < 0 || height < 0) {
    return 0;
  }
  return width / height;
}
